import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

// A lightweight linked-particle cursor trail rendered with Three.js WebGL.
// Not as feature-rich as the original LinkedParticles example but captures
// a similar aesthetic: glowing particles that drift in a turbulence-like
// field while thin quads link each particle to its two nearest neighbours.

export default function MagicalCursor() {
  const container = useRef(null);

  useEffect(() => {
    const maxParticles = 2048; // power of two keeps buffers tidy
    const particleLife = 1.5;  // seconds
    const spawnPerMove = 6;    // how many particles create at each pointer event

    // ------------ renderer / scene / camera ------------
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.autoClear = false;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(
      -window.innerWidth / 2,
      window.innerWidth / 2,
      window.innerHeight / 2,
      -window.innerHeight / 2,
      -1000,
      1000
    );

    if (container.current) {
      container.current.appendChild(renderer.domElement);
    }

    // ------------ particle geometry ------------
    const positions = new Float32Array(maxParticles * 3); // xyz
    const colors = new Float32Array(maxParticles * 3);
    const baseColors = new Float32Array(maxParticles * 3); // store original so we can attenuate
    const scales = new Float32Array(maxParticles);

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geom.setAttribute('aScale', new THREE.BufferAttribute(scales, 1));

    const sprite = new THREE.TextureLoader().load(
      'data:image/svg+xml;base64,PHN2ZyBpZD0ic3BhcmtsZSIgdmlld0JveD0iMCAwIDEwIDEwIiBmaWxsPSJ3aGl0ZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSI1IiBjeT0iNSIgcj0iNSIvPjwvc3ZnPg=='
    );

    const mat = new THREE.PointsMaterial({
      size: 3,
      transparent: true,
      depthWrite: false,
      vertexColors: true,
      map: sprite,
      blending: THREE.AdditiveBlending,
      emissive: new THREE.Color(0xffffff),
      emissiveIntensity: 10,
      toneMapped: false, // Prevents tone mapping to allow full brightness
    });

    const points = new THREE.Points(geom, mat);
    scene.add(points);

    // ------------ link geometry (line segments) ------------
    const linkPositions = new Float32Array(maxParticles * 2 * 3); // two vertices per link
    const linkColors = new Float32Array(maxParticles * 2 * 3);   // per-vertex rgb for fading
    const linkGeom = new THREE.BufferGeometry();
    linkGeom.setAttribute('position', new THREE.BufferAttribute(linkPositions, 3));
    linkGeom.setAttribute('color', new THREE.BufferAttribute(linkColors, 3));
    const linkMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const linkMesh = new THREE.LineSegments(linkGeom, linkMat);
    scene.add(linkMesh);

    // ------------ state arrays ------------
    const vel = new Float32Array(maxParticles * 2); // vx, vy per particle (z zero)
    const life = new Float32Array(maxParticles);   // remaining life seconds

    let head = 0; // circular buffer index
    let active = 0; // number of active particles (<= maxParticles)

    const colorPalette = [
      new THREE.Color('#ffffff'), // cyan
     // new THREE.Color('#7fffd4'), // aquamarine
    //   new THREE.Color('#32cd32'), // lime green
      new THREE.Color('#4B0082'), // dark purple
      //add a orange yellow like a candle
      new THREE.Color('#ffa500'), // orange
    ];

    const tmpColor = new THREE.Color();

    // ------------ pointer handling ------------
    let pointerX = 0;
    let pointerY = 0;

    const onMove = (e) => {
      pointerX = e.clientX - window.innerWidth / 2;
      pointerY = -(e.clientY - window.innerHeight / 2);

      // spawn particles
      for (let i = 0; i < spawnPerMove; i++) {
        spawnParticle(pointerX, pointerY);
      }
    };

    window.addEventListener('pointermove', onMove);

    function spawnParticle(x, y) {
      const idx = head;

      positions[idx * 3] = x;
      positions[idx * 3 + 1] = y;
      positions[idx * 3 + 2] = 0;

      // small random velocity
      const angle = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 40;
      vel[idx * 2] = Math.cos(angle) * speed;
      vel[idx * 2 + 1] = Math.sin(angle) * speed;

      // life
      life[idx] = particleLife;

      // scale
      scales[idx] = 0.5 + Math.random() * 0.5;

      // color
      tmpColor.copy(colorPalette[Math.floor(Math.random() * colorPalette.length)]);
      baseColors[idx * 3] = tmpColor.r;
      baseColors[idx * 3 + 1] = tmpColor.g;
      baseColors[idx * 3 + 2] = tmpColor.b;

      // initialize colors with full brightness
      colors[idx * 3] = tmpColor.r;
      colors[idx * 3 + 1] = tmpColor.g;
      colors[idx * 3 + 2] = tmpColor.b;

      head = (head + 1) % maxParticles;
      active = Math.min(active + 1, maxParticles);
    }

    // ------------ animation ------------
    let lastTime = performance.now();
    const animate = () => {
      const now = performance.now();
      const dt = (now - lastTime) / 1000; // seconds
      lastTime = now;

      // update particles
      const range = active;
      let linkPosIdx = 0;
      let linkColorIdx = 0;
      let lastAlive = -1; // track previous alive index to create segments

      for (let i = 0; i < range; i++) {
        life[i] -= dt;

        if (life[i] <= 0) {
          // dead particle -> zero attributes
          positions[i * 3] = positions[i * 3 + 1] = positions[i * 3 + 2] = 0;
          scales[i] = 0;
          colors[i * 3] = colors[i * 3 + 1] = colors[i * 3 + 2] = 0;
          continue; // skip further processing / no links
        }

        // ------------- update live particle -------------

        // velocity with friction & turbulence
        vel[i * 2] *= 0.97;
        vel[i * 2 + 1] *= 0.97;
        const t = now * 0.001 + i;
        vel[i * 2] += Math.sin(t * 1.3 + i) * 15 * dt;
        vel[i * 2 + 1] += Math.cos(t * 1.7 + i) * 15 * dt;

        positions[i * 3] += vel[i * 2] * dt;
        positions[i * 3 + 1] += vel[i * 2 + 1] * dt;

        // dot brightness fade (slow)
        const fadeFrac = life[i] / particleLife;
        const dotBright = Math.sqrt(fadeFrac);
        colors[i * 3] = baseColors[i * 3] * dotBright;
        colors[i * 3 + 1] = baseColors[i * 3 + 1] * dotBright;
        colors[i * 3 + 2] = baseColors[i * 3 + 2] * dotBright;
        scales[i] = dotBright * 1.5;

        // ------------- build link to previous alive particle -------------
        if (lastAlive !== -1) {
          const br = fadeFrac * fadeFrac; // quick fade for lines

          // current vertex
          linkPositions[linkPosIdx++] = positions[i * 3];
          linkPositions[linkPosIdx++] = positions[i * 3 + 1];
          linkPositions[linkPosIdx++] = 0;
          linkColors[linkColorIdx++] = br;
          linkColors[linkColorIdx++] = br;
          linkColors[linkColorIdx++] = br;

          // previous vertex
          linkPositions[linkPosIdx++] = positions[lastAlive * 3];
          linkPositions[linkPosIdx++] = positions[lastAlive * 3 + 1];
          linkPositions[linkPosIdx++] = 0;
          linkColors[linkColorIdx++] = br;
          linkColors[linkColorIdx++] = br;
          linkColors[linkColorIdx++] = br;
        }

        // current becomes lastAlive for next iteration
        lastAlive = i;
      }

      geom.attributes.position.needsUpdate = true;
      geom.attributes.color.needsUpdate = true;
      geom.attributes.aScale.needsUpdate = true;

      // update draw range to only render actual link vertices
      linkGeom.setDrawRange(0, linkPosIdx / 3);
      linkGeom.attributes.position.needsUpdate = true;
      linkGeom.attributes.color.needsUpdate = true;

      // render
      renderer.clear();
      renderer.render(scene, camera);

      requestAnimationFrame(animate);
    };

    animate();

    // ------------ resize ------------
    const resize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      camera.left = -window.innerWidth / 2;
      camera.right = window.innerWidth / 2;
      camera.top = window.innerHeight / 2;
      camera.bottom = -window.innerHeight / 2;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', resize);

    // cleanup
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('resize', resize);
      renderer.dispose();
      geom.dispose();
      linkGeom.dispose();
      mat.dispose();
      linkMat.dispose();
      if (container.current) container.current.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={container} style={{ position: 'fixed', inset: 0, pointerEvents: 'none' }} />;
}

